import type { TokenUsageStats } from '../../services/api';

/** Visual states that drive meter colour. */
export type MeterState = 'healthy' | 'warning' | 'paid' | 'exhausted' | 'unknown';

export const METER_COLORS: Record<MeterState, string> = {
  healthy: '#0D8A6A',
  warning: '#d9a60c',
  paid: '#e88845',
  exhausted: '#D85A30',
  unknown: '#B89088',
};

/**
 * Chooses the visual state for the meter from backend flags. We treat
 * "daily free exhausted but purchased credits still cover you" as `paid`
 * (orange accent) to signal the source of tokens changed.
 */
export const getMeterState = (
  stats: TokenUsageStats | null,
  isDemo: boolean
): MeterState => {
  if (isDemo) return 'healthy';
  if (!stats) return 'unknown';
  if (stats.is_quota_exceeded) return 'exhausted';
  if (stats.is_daily_quota_exhausted && stats.remaining_purchased_tokens > 0) return 'paid';
  if (stats.show_warning) return 'warning';
  return 'healthy';
};

/** Percentage of the daily free pool consumed (0–100). */
export const getDailyUsagePercent = (stats: TokenUsageStats | null): number => {
  if (!stats || stats.daily_free_quota <= 0) return 0;
  return Math.min(100, Math.max(0, stats.daily_free_usage_percent || 0));
};

/**
 * Percentage the meter bar should fill. The bar's semantics change with state:
 *  - `healthy`/`warning`: daily free consumption (bar fills as daily pool drains)
 *  - `paid`:              today's purchased-credit consumption (fills as paid pool drains)
 *  - `exhausted`:         100% (bar fully filled red)
 *  - `unknown`/demo:      0
 * This lets the colour AND the fill level together communicate "which pool
 * you're on and how much of it is left".
 */
export const getMeterPercent = (
  stats: TokenUsageStats | null,
  state: MeterState,
  isDemo: boolean
): number => {
  if (isDemo || !stats) return 0;
  if (state === 'exhausted') return 100;
  if (state === 'paid') {
    const totalPurchasedToday =
      stats.remaining_purchased_tokens + stats.purchased_tokens_used_today;
    if (totalPurchasedToday <= 0) return 100;
    return Math.min(
      100,
      Math.max(0, (stats.purchased_tokens_used_today / totalPurchasedToday) * 100)
    );
  }
  return getDailyUsagePercent(stats);
};

/** `1,234,567` → `1.2M` / `12.3k` / `987`. */
export const formatAbbrev = (num: number): string => {
  if (!Number.isFinite(num) || num <= 0) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}k`;
  return String(Math.round(num));
};

export const formatFull = (num: number): string =>
  Number.isFinite(num) ? num.toLocaleString() : '0';

/**
 * Computes a human-readable countdown from now → `iso` timestamp.
 * Returns empty string when no timestamp is provided or reset is in the past.
 */
export const formatResetCountdown = (iso?: string | null): string => {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diffMs = ts - Date.now();
  if (diffMs <= 0) return 'any moment';
  const mins = Math.floor(diffMs / 60_000);
  const hrs = Math.floor(mins / 60);
  if (hrs >= 1) {
    const rem = mins % 60;
    return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
  }
  return `${mins}m`;
};
